import { TextMeasurer } from './fit-text';

export interface FontMetrics {
  /** Font ascent above the alphabetic baseline, px — the top of the CSS content area. */
  ascent: number;
  descent: number;
}

// Used only where the canvas has no font metrics (jsdom); real browsers report them.
const FALLBACK_ASCENT = 0.9;
const FALLBACK_DESCENT = 0.25;

/**
 * Ascent and descent of the font set on `measurer`, the same numbers CSS lays a line out with
 * (`TextMetrics.fontBoundingBoxAscent` / `Descent`, measured from the alphabetic baseline). `sizePx` is only the
 * fallback's scale.
 */
export function fontMetrics(measurer: TextMeasurer, sizePx: number): FontMetrics {
  const metrics = measurer.measureText('Hg') as Partial<TextMetrics>;
  const ascent = metrics.fontBoundingBoxAscent;
  const descent = metrics.fontBoundingBoxDescent;
  return typeof ascent === 'number' && typeof descent === 'number' && ascent + descent > 0
    ? { ascent, descent }
    : { ascent: sizePx * FALLBACK_ASCENT, descent: sizePx * FALLBACK_DESCENT };
}

/** Height of one line: the given CSS `line-height`, or `normal` — ascent plus descent. */
export function lineHeightOf(metrics: FontMetrics, lineHeight?: number): number {
  return lineHeight ?? metrics.ascent + metrics.descent;
}

/**
 * The alphabetic baseline of a line whose box starts at `top`, placed as CSS does it: the content area
 * (ascent + descent) is centred in the line box, the rest split evenly above and below (half-leading).
 * Draw with `textBaseline = 'alphabetic'`.
 */
export function baselineFromTop(top: number, metrics: FontMetrics, lineHeight?: number): number {
  const height = lineHeightOf(metrics, lineHeight);
  return top + (height - (metrics.ascent + metrics.descent)) / 2 + metrics.ascent;
}
